import React, { useCallback, useMemo } from 'react';
import { DataTable } from 'src/common/components/table/data-table';
import { generateColumn } from 'src/common/components/table/LegacyTable';
import { TooltipComponent } from 'src/common/tooltip/component';
// import { TableSortableColumnHeader } from 'src/common/components/table/sortable-column-header';
import { DotSmallIcon, DoubleChatStrongIcon, LinkRegularIcon } from 'src/monet/icons';
import { fromNowInUtc } from 'src/utils/date';
// import { functionNoop } from 'src/utils/function/noop';
import { withFixedLoadingSpinner } from 'src/common/components/loading-spinner/hooks/with-loading-spinner';
import { getListConversationText } from '../helpers/get-list-conversation-text';
import { DashboardInboxMenuActions } from '../menu/actions';
import { InboxMessageStack, useDashboardInboxStore } from '../store';

const LocationElement = ({ stack }: { stack: InboxMessageStack }) => {
  // icon seems a bit off between two inline texts, viewport needs to be set manually.
  const linkIconElementRef = useCallback((element: SVGSVGElement) => {
    element?.setAttribute('viewBox', '0 0 18 18');
  }, []);
  return (
    <div className='inline gap-1 text-bedrock-p'>
      <span className='text-bedrock-black'>{stack.location}</span>
      <div className='inline-flex ml-1 items-baseline gap-1 shrink-0 text-bedrock-dark-gray'>
        <LinkRegularIcon ref={linkIconElementRef} className='w-3.5 h-3.5' />
        <span>{stack.alias.name}</span>
      </div>
    </div>
  );
};

export interface Props {
  // onTableHeaderClick: (params: InboxListParams) => void;
  messages: InboxMessageStack[];
  onRowClick: (rowIndex: number) => void;
}

const DashboardInboxMessagesListComponent = ({ messages, onRowClick }: Props) => {
  const { listParams } = useDashboardInboxStore();

  const ListColumns = useMemo(
    () => [
      generateColumn({
        Header: <DoubleChatStrongIcon className='w-4 h-4 m-auto' />,
        accessor: 'read',
        width: 60,
        allowShrinking: false,
        Cell: ({ index }: { index: number }) => (
          <div className='h-[22px]'>
            {messages[index].hasUnreadMessage ? (
              <DotSmallIcon className='h-full w-2 m-auto text-bedrock-brand-secondary' />
            ) : null}
          </div>
        ),
      }),
      generateColumn({
        Header: 'Date',
        accessor: 'date',
        width: 130,
        Cell: ({ index }: { index: number }) => <span>{fromNowInUtc(messages[index].updatedAt)}</span>,
      }),
      generateColumn({
        Header: 'Conversation',
        accessor: 'members',
        width: 220,
        Cell: ({ index }: { index: number }) => (
          <span className='break-words'>{getListConversationText(messages[index].members)}</span>
        ),
      }),
      generateColumn({
        Header: () => (
          <div className='flex'>
            <TooltipComponent placement='top' tooltipContent='Journey / Step / Link' styling='tooltip' theme='journey'>
              Location
            </TooltipComponent>
          </div>
        ),
        accessor: 'location',
        width: 500,
        allowShrinking: true,
        Cell: ({ index }: { index: number }) => <LocationElement stack={messages[index]} />,
      }),
      generateColumn({
        Header: '',
        accessor: 'actions',
        width: 'auto',
        allowShrinking: false,
        Cell: ({ index }: { index: number }) => {
          return (
            <div className='text-bedrock-p text-bedrock-black mr-2'>
              <div className='flex grow-0 justify-end'>
                <DashboardInboxMenuActions status={messages[index].status} />
              </div>
            </div>
          );
        },
      }),
    ],
    [listParams.orderBy, listParams.sortName, messages]
  );

  return (
    <DataTable
      tableClassName='max-h-[52rem] w-full max-w-full'
      columns={ListColumns}
      data={messages}
      onRowClick={({ index }) => onRowClick(index)}
    />
  );
};

export const DashboardInboxMessagesList = withFixedLoadingSpinner<Props>(DashboardInboxMessagesListComponent);
